import {
  externalPlanStatusSchema,
  type CreateExternalPlanDto,
  type ExternalPlanStatusSchema,
  type QueryExternalPlansDto,
  type UpdateExternalPlanDto,
} from '@control-center/shared';
import { z } from 'zod';

import { apiClient } from './api-client';

// =============================================================================
// Response schemas — qa-hardening 2026-05-28
// =============================================================================

const externalPlanEntitySchema = z.object({
  id: z.string().uuid(),
  saasProductId: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: externalPlanStatusSchema,
  lastSyncedAt: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const listResponseSchema = z.object({
  items: z.array(externalPlanEntitySchema),
  meta: z.object({
    next_cursor: z.string().nullable(),
    total_estimate: z.number().nullable(),
  }),
});

const singleResponseSchema = z.object({ data: externalPlanEntitySchema });

export interface ExternalPlanEntity {
  id: string;
  saasProductId: string;
  slug: string;
  name: string;
  description: string | null;
  status: ExternalPlanStatusSchema;
  lastSyncedAt: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  items: ExternalPlanEntity[];
  meta: { next_cursor: string | null; total_estimate: number | null };
}

export async function fetchExternalPlans(
  params: QueryExternalPlansDto = { limit: 50 },
): Promise<ListResponse> {
  const { data } = await apiClient.get('/api/external-plans', { params });
  return listResponseSchema.parse(data);
}

export async function fetchExternalPlan(id: string): Promise<ExternalPlanEntity> {
  const { data } = await apiClient.get(`/api/external-plans/${id}`);
  return singleResponseSchema.parse(data).data;
}

export async function createExternalPlan(
  payload: CreateExternalPlanDto,
): Promise<ExternalPlanEntity> {
  const { data } = await apiClient.post('/api/external-plans', payload);
  return singleResponseSchema.parse(data).data;
}

export async function updateExternalPlan(
  id: string,
  payload: UpdateExternalPlanDto,
): Promise<ExternalPlanEntity> {
  const { data } = await apiClient.patch(`/api/external-plans/${id}`, payload);
  return singleResponseSchema.parse(data).data;
}

export async function deleteExternalPlan(id: string): Promise<ExternalPlanEntity> {
  const { data } = await apiClient.delete(`/api/external-plans/${id}`);
  return singleResponseSchema.parse(data).data;
}
